import { gapi } from 'gapi-script';
import dayjs from 'dayjs';
import GoogleAPI, { CellFormula, CellFormatRange } from './GoogleAPI';
import { sanitizeFileName, resolveDisplayPartNoByKey } from './utils';
import { PartNo } from '../contexts/types';

/** WebAPIContext.request関数の型 */
type WebApiRequest = (payload: { query: string; variables?: Record<string, any> }) => Promise<any>;

/** BOMスプレッドシート格納先パス */
const SPREAD_SHEET_PATH = "CEAS/SpreadSheet/ProductionProcess";

/** BOMスナップショット用シート名 */
const SNAPSHOT_SHEET_NAMES = {
  processBom: "ProcessBOM(initial)",
  materialCost: "MaterialCost",
  cycletime: "Cycletime",
  yield: "Yield",
};

/** BOMデータ（DB取得時の型） */
interface BomDataRow {
  seq: string;
  level: string;
  modelNo: string;
  processCode: string;
  itemNo: string;
  itemName: string;
  usage: string;
  isCustomerSpecifiedParts: string;
  purchaseCurrencyId: string;
  unitPriceCombinedMonthly: string;
  unitPriceInhouseMonthly: string;
  unitPriceExternalMonthly: string;
  inhouseRatioMonthly: string;
  materialYieldMonthly: string;
  amountMonthly: string;
  cycleTimeMonthly: string;
  yieldMonthly: string;
}

/**
 * Google SpreadsheetをExcel(.xlsx)としてエクスポートし、Base64文字列で返す
 *
 * @param spreadsheetId Google SpreadsheetのファイルID
 * @returns Base64エンコード済み文字列（data URI prefix なし）
 */
const exportSpreadsheetAsExcelBase64 = async (spreadsheetId: string): Promise<string> => {
  const accessToken = gapi.client.getToken()?.access_token;
  if (!accessToken) throw new Error("Not authenticated");
  const url = `https://www.googleapis.com/drive/v3/files/${spreadsheetId}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status} ${response.statusText}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  // Base64エンコード
  let binary = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
};

/**
 * DBからBOMデータを取得する
 */
const fetchBomDataFromDb = async (webApiRequest: WebApiRequest, projectId: string, partNo: string, reviewDate?: string): Promise<BomDataRow[]> => {
  const query = `
    query getBomByProjectId($projectId: String, $partNo: String, $reviewDate: String) {
      getBomByProjectId(projectId: $projectId, partNo: $partNo, reviewDate: $reviewDate) {
        seq
        level
        modelNo
        processCode
        itemNo
        itemName
        usage
        isCustomerSpecifiedParts
        purchaseCurrencyId
        unitPriceCombinedMonthly
        unitPriceInhouseMonthly
        unitPriceExternalMonthly
        inhouseRatioMonthly
        materialYieldMonthly
        amountMonthly
        cycleTimeMonthly
        yieldMonthly
      }
    }
  `;

  const variables: Record<string, any> = { projectId, partNo };
  if (reviewDate) {
    variables.reviewDate = reviewDate;
  }

  const result = await webApiRequest({ query, variables });
  return result?.data?.getBomByProjectId || [];
};

/**
 * 通貨マスタをDBから取得し、currencyId→currencyName のマッピングを返す
 */
const fetchCurrencyData = async (webApiRequest: WebApiRequest, projectId: string): Promise<Record<string, string>> => {
  const query = `
    query getCurrencyById($projectId: String) {
      getCurrencyById(projectId: $projectId) {
        currencyId
        currencyName
      }
    }
  `;

  try {
    const result = await webApiRequest({ query, variables: { projectId } });
    const currencyList = result?.data?.getCurrencyById || [];
    const map: Record<string, string> = {};
    for (const c of currencyList) {
      if (c.currencyId && c.currencyName) {
        map[c.currencyId] = c.currencyName;
      }
    }
    return map;
  } catch (error) {
    console.warn("Failed to fetch currency data, using IDs as-is:", error);
    return {};
  }
};

/**
 * JSONの月次データをパースして { "YYYY-MM": value } のオブジェクトを返す
 */
const parseMonthly = (json: string | null | undefined): Record<string, string> => {
  try {
    if (!json || json === "") return {};
    return JSON.parse(json) as Record<string, string>;
  } catch {
    return {};
  }
};

/**
 * 値を100で割った文字列を返す（比率・歩留まりの百分率→小数変換用）
 * BomInfoList.tsxと同様に、DB上の百分率値（例: 99.86）を小数値（例: 0.9986）に変換する
 */
const div100 = (value: string): string => {
  if (!value || value === "") return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  return String(num / 100);
};

/**
 * 1-based列番号をスプレッドシートの列文字に変換する（1=A, 26=Z, 27=AA, ...）
 */
const columnToLetter = (col: number): string => {
  let letter = '';
  while (col > 0) {
    const mod = (col - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    col = Math.floor((col - 1) / 26);
  }
  return letter;
};

/**
 * プロジェクトの開始日〜終了日から年月リスト（YYYY-MM）を生成する
 *
 * BomInfoList.tsx:2760 の `generateYearMonthList` と同一ロジック。
 * スナップショット出力を BomInfoList の表示と完全一致させるため、BOMデータではなく
 * プロジェクト期間を元に年月列を構築する。
 *
 * @param startDate プロジェクト開始日（YYYY-MM-DD または YYYY/MM/DD）
 * @param endDate プロジェクト終了日
 * @returns 年月リスト（YYYY-MM形式の配列）
 */
const generateYearMonthList = (startDate: string, endDate: string): string[] => {
  const result: string[] = [];

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) return result;
  if (start.isAfter(end)) return result;

  let current = start.startOf("month");
  const endMonth = end.startOf("month");

  while (current.isBefore(endMonth) || current.isSame(endMonth)) {
    result.push(current.format("YYYY-MM"));
    current = current.add(1, "month");
  }

  return result;
};

/**
 * DBのBOMデータからスナップショット用スプレッドシートを一時生成する
 *
 * BomInfoListと同じ4シート構造（ProcessBOM, MaterialCost, Cycletime, Yield）で作成し、
 * ヘッダー行 + データ行 + 数式 + 書式設定を書き込む（BomInfoListと同一の出力を生成）。
 */
const createSnapshotSpreadsheet = async (
  googleAPI: GoogleAPI,
  projectId: string,
  displayPartNo: string,
  bomData: BomDataRow[],
  currencyMap: Record<string, string>,
  yearMonths: string[],
  parentId: string,
): Promise<string> => {
  // スプレッドシート作成（フォルダパス解決は呼び出し側で実施済み・partNo全件で同一のため）
  const fileName = sanitizeFileName(displayPartNo);
  // BOM編集画面（BomInfoList）と同一フォルダ・同一ファイル名のため、存在確認を省略して常に新規作成する
  // （既存SSを流用すると、編集中データの混入およびExcelエクスポート後の削除でBOM編集側のSSが消える）
  const spreadsheetId = await googleAPI.createSpreadSheet(fileName, parentId, null, { skipExistCheck: true });

  try {
    // シート追加
    await googleAPI.addSheets(spreadsheetId, [
      SNAPSHOT_SHEET_NAMES.processBom,
      SNAPSHOT_SHEET_NAMES.materialCost,
      SNAPSHOT_SHEET_NAMES.cycletime,
      SNAPSHOT_SHEET_NAMES.yield,
    ]);

    // シートID情報を取得（数式設定・書式設定に必要）
    const spreadsheetInfo = await googleAPI.getSpreadSheetInfo(spreadsheetId);
    const sheetNameToId: { [key: string]: number } = {};
    for (const sheet of spreadsheetInfo.sheets || []) {
      if (sheet.properties?.title && sheet.properties?.sheetId !== undefined) {
        sheetNameToId[sheet.properties.title] = sheet.properties.sheetId;
      }
    }
    const sheetIds = {
      materialCost: sheetNameToId[SNAPSHOT_SHEET_NAMES.materialCost],
      cycletime: sheetNameToId[SNAPSHOT_SHEET_NAMES.cycletime],
      yield: sheetNameToId[SNAPSHOT_SHEET_NAMES.yield],
    };

    // 共通ヘッダー（年月リストは呼び出し側でプロジェクト期間から生成したものを使用）
    const commonHeaders = ["Seq.", "Level", "Model No.", "Process Code", "Item No.", "Item Name"];
    const getCommonValues = (row: BomDataRow) => [
      row.seq || "", row.level || "", row.modelNo || "",
      row.processCode || "", row.itemNo || "", row.itemName || "",
    ];

    // ■ ProcessBOM(initial) シート
    const processBomHeader = [...commonHeaders, "客先指定部品"];
    const processBomRows = bomData.map(row => [
      ...getCommonValues(row),
      row.isCustomerSpecifiedParts === "true" ? "TRUE" : "",
    ]);
    // partNo行をヘッダーと同じ幅にパディング（generateRangeがvalues[0]の長さでカラム数を計算するため）
    const processBomPartNoRow = [displayPartNo, ...Array(processBomHeader.length - 1).fill("")];
    await googleAPI.spreadSheetSetValue(spreadsheetId, SNAPSHOT_SHEET_NAMES.processBom, 1, 1, [
      processBomPartNoRow,
      processBomHeader,
      ...processBomRows,
    ]);

    // ■ MaterialCost シート（BomInfoList.tsxと同じ構造）
    // ヘッダー: 共通列 + 購入通貨 + Usage + Type + 年月列
    const materialCostFixedHeaders = [...commonHeaders, "購入通貨", "Usage", "Type"];
    const materialCostHeader = [...materialCostFixedHeaders, ...yearMonths];

    // データ行構築
    const mcDataRows: string[][] = [];
    const mcPercentRows: number[] = []; // PERCENT書式を適用するデータ行の1-based行番号
    const mcDataStartRow = 6; // 数式ラベル4行 + ヘッダー1行 の次がデータ開始
    const mcEmptyPrefix = Array(commonHeaders.length + 2).fill(""); // 共通列 + 購入通貨 + Usage の空埋め
    for (const row of bomData) {
      const common = getCommonValues(row);
      const inhouseMonthly = parseMonthly(row.unitPriceInhouseMonthly);
      const externalMonthly = parseMonthly(row.unitPriceExternalMonthly);
      const ratioMonthly = parseMonthly(row.inhouseRatioMonthly);
      const matYieldMonthly = parseMonthly(row.materialYieldMonthly);

      const hasMaterialCost = [inhouseMonthly, externalMonthly, ratioMonthly, matYieldMonthly]
        .some(m => Object.values(m).some(v => v !== ""));

      if (hasMaterialCost) {
        // 購入通貨IDを通貨名に変換
        const currencyName = currencyMap[row.purchaseCurrencyId] || row.purchaseCurrencyId || "";
        // Inhouse Ratio / Material Yield 行のPERCENT書式適用行を記録
        mcPercentRows.push(mcDataStartRow + mcDataRows.length + 2); // Inhouse Ratio（3行目）
        mcPercentRows.push(mcDataStartRow + mcDataRows.length + 3); // Material Yield（4行目）
        // 材料費行: 4行出力（Type列にタイプ名、ratio/yieldは100分割）
        mcDataRows.push([...common, currencyName, row.usage || "", "U/P(inhouse)", ...yearMonths.map(ym => inhouseMonthly[ym] || "")]);
        mcDataRows.push([...mcEmptyPrefix, "U/P(external)", ...yearMonths.map(ym => externalMonthly[ym] || "")]);
        mcDataRows.push([...mcEmptyPrefix, "Inhouse Ratio", ...yearMonths.map(ym => div100(ratioMonthly[ym] || ""))]);
        mcDataRows.push([...mcEmptyPrefix, "Material Yield", ...yearMonths.map(ym => div100(matYieldMonthly[ym] || ""))]);
      } else {
        // プロセス行: 1行（Type・年月列は空）
        mcDataRows.push([...common, "", row.usage || "", "", ...Array(yearMonths.length).fill("")]);
      }
    }

    // ラベル行（数式はspreadSheetSetFormulasで別途設定）
    const mcLabelBase = Array(materialCostFixedHeaders.length - 1).fill("");
    const mcFirstYmCol = materialCostFixedHeaders.length + 1; // 年月列の開始列番号（1-based = 10 = J）
    const materialCostAllRows: string[][] = [
      [...mcLabelBase, "U/P(inhouse)", ...Array(yearMonths.length).fill("")],
      [...mcLabelBase, "U/P(external)", ...Array(yearMonths.length).fill("")],
      [...mcLabelBase, "Inhouse Ratio", ...Array(yearMonths.length).fill("")],
      [...mcLabelBase, "Material Yield", ...Array(yearMonths.length).fill("")],
      materialCostHeader,
      ...mcDataRows,
    ];
    await googleAPI.spreadSheetSetValue(spreadsheetId, SNAPSHOT_SHEET_NAMES.materialCost, 1, 1, materialCostAllRows);

    // ■ Cycletime シート（BomInfoList.tsxと同じ構造: SUM行 + ラベル行 + ヘッダー + データ）
    const cycletimeHeader = [...commonHeaders, ...yearMonths];
    const cycletimeRows = bomData.map(row => {
      const monthly = parseMonthly(row.cycleTimeMonthly);
      return [...getCommonValues(row), ...yearMonths.map(ym => monthly[ym] || "")];
    });
    // 数式行は空で書き込み、spreadSheetSetFormulasで別途設定
    const ctFirstYmCol = commonHeaders.length + 1; // 年月列の開始列番号（1-based = 7 = G）
    const ctFormulaRow = Array(cycletimeHeader.length).fill("");
    // ラベル行はヘッダーと同じ列数に揃える（yearMonths=0の場合に列数不整合でAPIエラーになるのを防止）
    const cycletimeLabelRow = Array(cycletimeHeader.length).fill("");
    if (yearMonths.length > 0) cycletimeLabelRow[commonHeaders.length] = "CycleTime";
    await googleAPI.spreadSheetSetValue(spreadsheetId, SNAPSHOT_SHEET_NAMES.cycletime, 1, 1, [
      ctFormulaRow,
      cycletimeLabelRow,
      cycletimeHeader,
      ...cycletimeRows,
    ]);

    // ■ Yield シート（BomInfoList.tsxと同じ構造: PRODUCT行 + ラベル行 + ヘッダー + データ、値は100分割）
    const yieldHeader = [...commonHeaders, ...yearMonths];
    const yieldRows = bomData.map(row => {
      const monthly = parseMonthly(row.yieldMonthly);
      return [...getCommonValues(row), ...yearMonths.map(ym => div100(monthly[ym] || ""))];
    });
    // 数式行は空で書き込み、spreadSheetSetFormulasで別途設定
    const yieldFirstYmCol = commonHeaders.length + 1; // 年月列の開始列番号（1-based = 7 = G）
    const yieldFormulaRow = Array(yieldHeader.length).fill("");
    // ラベル行はヘッダーと同じ列数に揃える（yearMonths=0の場合に列数不整合でAPIエラーになるのを防止）
    const yieldLabelRow = Array(yieldHeader.length).fill("");
    if (yearMonths.length > 0) yieldLabelRow[commonHeaders.length] = "Yield";
    await googleAPI.spreadSheetSetValue(spreadsheetId, SNAPSHOT_SHEET_NAMES.yield, 1, 1, [
      yieldFormulaRow,
      yieldLabelRow,
      yieldHeader,
      ...yieldRows,
    ]);

    // ■ 数式設定（BomInfoList.tsxと同一の数式をspreadSheetSetFormulasで設定）
    const typeColLetter = columnToLetter(materialCostFixedHeaders.length); // Type列 = I

    // MaterialCost: Row 1-2 SUMIF, Row 3-4 PRODUCT(FILTER)
    const mcFormulas: CellFormula[] = [];
    yearMonths.forEach((_, idx) => {
      const col = mcFirstYmCol + idx;
      const colLetter = columnToLetter(col);
      // Row 1: U/P(inhouse) — =sumif(I$6:I,"U/P(inhouse)",J$6:J)
      mcFormulas.push({ sheetId: sheetIds.materialCost, row: 1, col, formula: `=sumif(${typeColLetter}$${mcDataStartRow}:${typeColLetter},"U/P(inhouse)",${colLetter}$${mcDataStartRow}:${colLetter})` });
      // Row 2: U/P(external) — =sumif(I$6:I,"U/P(external)",J$6:J)
      mcFormulas.push({ sheetId: sheetIds.materialCost, row: 2, col, formula: `=sumif(${typeColLetter}$${mcDataStartRow}:${typeColLetter},"U/P(external)",${colLetter}$${mcDataStartRow}:${colLetter})` });
      // Row 3: Inhouse Ratio — =iferror(product(filter(J$6:J, I$6:I="Inhouse Ratio")),"")
      mcFormulas.push({ sheetId: sheetIds.materialCost, row: 3, col, formula: `=iferror(product(filter(${colLetter}$${mcDataStartRow}:${colLetter}, ${typeColLetter}$${mcDataStartRow}:${typeColLetter}="Inhouse Ratio")),"")` });
      // Row 4: Material Yield — =iferror(product(filter(J$6:J, I$6:I="Material Yield")),"")
      mcFormulas.push({ sheetId: sheetIds.materialCost, row: 4, col, formula: `=iferror(product(filter(${colLetter}$${mcDataStartRow}:${colLetter}, ${typeColLetter}$${mcDataStartRow}:${typeColLetter}="Material Yield")),"")` });
    });

    // Cycletime: Row 1 — =sum(G4:G)
    const ctFormulas: CellFormula[] = [];
    yearMonths.forEach((_, idx) => {
      const col = ctFirstYmCol + idx;
      const colLetter = columnToLetter(col);
      ctFormulas.push({ sheetId: sheetIds.cycletime, row: 1, col, formula: `=sum(${colLetter}4:${colLetter})` });
    });

    // Yield: Row 1 — =product(G4:G)
    const yieldFormulas: CellFormula[] = [];
    yearMonths.forEach((_, idx) => {
      const col = yieldFirstYmCol + idx;
      const colLetter = columnToLetter(col);
      yieldFormulas.push({ sheetId: sheetIds.yield, row: 1, col, formula: `=product(${colLetter}4:${colLetter})` });
    });

    await googleAPI.spreadSheetSetFormulas(spreadsheetId, [...mcFormulas, ...ctFormulas, ...yieldFormulas]);

    // ■ 書式設定（BomInfoList.tsxと同一の書式をspreadSheetSetCellFormatで設定）
    const formatRanges: CellFormatRange[] = [];
    const offsetColumn = commonHeaders.length + 1; // 年月列の開始位置（全シート共通、BomInfoListのoffsetColumn相当）

    // MaterialCost Row 1-2: NUMBER "0"（U/P合計行）
    formatRanges.push({
      sheetId: sheetIds.materialCost, startRow: 1, endRow: 2,
      startCol: mcFirstYmCol, endCol: mcFirstYmCol + yearMonths.length,
      format: { numberFormat: { type: "NUMBER", pattern: "0" } },
    });
    // MaterialCost Row 3: PERCENT "0.00%"（Inhouse Ratio合計行）
    formatRanges.push({
      sheetId: sheetIds.materialCost, startRow: 3, endRow: 3,
      startCol: mcFirstYmCol, endCol: mcFirstYmCol + yearMonths.length,
      format: { numberFormat: { type: "PERCENT", pattern: "0.00%" } },
    });
    // MaterialCost Row 4: PERCENT "0.00%"（Material Yield合計行）
    formatRanges.push({
      sheetId: sheetIds.materialCost, startRow: 4, endRow: 4,
      startCol: mcFirstYmCol, endCol: mcFirstYmCol + yearMonths.length,
      format: { numberFormat: { type: "PERCENT", pattern: "0.00%" } },
    });
    // MaterialCost データ行: Inhouse Ratio / Material Yield 行にPERCENT書式
    for (const rowNum of mcPercentRows) {
      formatRanges.push({
        sheetId: sheetIds.materialCost, startRow: rowNum, endRow: rowNum,
        startCol: mcFirstYmCol, endCol: mcFirstYmCol + yearMonths.length,
        format: { numberFormat: { type: "PERCENT", pattern: "0.00%" } },
      });
    }

    // Yield Row 1: PERCENT "0.00%"（PRODUCT合計行）
    formatRanges.push({
      sheetId: sheetIds.yield, startRow: 1, endRow: 1,
      startCol: yieldFirstYmCol, endCol: yieldFirstYmCol + yearMonths.length,
      format: { numberFormat: { type: "PERCENT", pattern: "0.00%" } },
    });
    // Yield データ行: PERCENT "0.00%"
    if (bomData.length > 0) {
      formatRanges.push({
        sheetId: sheetIds.yield, startRow: 4, endRow: 4 + bomData.length - 1,
        startCol: yieldFirstYmCol, endCol: yieldFirstYmCol + yearMonths.length,
        format: { numberFormat: { type: "PERCENT", pattern: "0.00%" } },
      });
    }

    // 全3シート ヘッダー行の年月列: DATE "yyyy-mm"
    formatRanges.push(
      // MaterialCost（ヘッダーはRow 5）
      {
        sheetId: sheetIds.materialCost, startRow: 5, endRow: 5,
        startCol: offsetColumn, endCol: offsetColumn + yearMonths.length,
        format: { numberFormat: { type: "DATE", pattern: "yyyy-mm" } },
      },
      // Cycletime（ヘッダーはRow 3）
      {
        sheetId: sheetIds.cycletime, startRow: 3, endRow: 3,
        startCol: offsetColumn, endCol: offsetColumn + yearMonths.length,
        format: { numberFormat: { type: "DATE", pattern: "yyyy-mm" } },
      },
      // Yield（ヘッダーはRow 3）
      {
        sheetId: sheetIds.yield, startRow: 3, endRow: 3,
        startCol: offsetColumn, endCol: offsetColumn + yearMonths.length,
        format: { numberFormat: { type: "DATE", pattern: "yyyy-mm" } },
      },
    );

    await googleAPI.spreadSheetSetCellFormat(spreadsheetId, formatRanges);

  } catch (error) {
    // 不完全なスプレッドシートを削除して、次回実行時に誤ってエクスポートされるのを防ぐ
    try {
      await googleAPI.deleteFile(spreadsheetId);
      console.log(`Cleaned up incomplete spreadsheet: ${spreadsheetId}`);
    } catch (cleanupError) {
      console.error(`Failed to cleanup incomplete spreadsheet: ${spreadsheetId}`, cleanupError);
    }
    throw error;
  }

  return spreadsheetId;
};

/**
 * プロジェクトの全partNoについて、Google SpreadsheetをExcelエクスポートし
 * S3アップロード用のファイル配列を返す
 *
 * マイドライブにスプレッドシートが存在しない場合は、DBからBOMデータを取得して
 * スナップショット用スプレッドシートを一時生成した上でエクスポートする。
 *
 * @param projectId プロジェクトID
 * @param partNoList partNo一覧
 * @param webApiRequest WebAPIContextのrequest関数（GraphQL呼び出し用）
 * @param startDate プロジェクト開始日（YYYY-MM-DD）— 年月列生成に使用
 * @param endDate プロジェクト終了日（YYYY-MM-DD）— 年月列生成に使用
 * @param reviewDate 振り返り日（振り返りモード時のみ指定）。指定時は review_{reviewDate}/BomSnapshot に保存
 * @param partNoDetails displayPartNo解決用のPartNo一覧（No.435対応。ファイル名・シート表示のPartNoをdisplayPartNoに変換するために使用）
 * @returns S3アップロード用ファイル配列（folderName, fileName, contentType, fileContent）
 */
export const exportBomSnapshotFiles = async (
  projectId: string,
  partNoList: string[],
  webApiRequest: WebApiRequest,
  startDate: string,
  endDate: string,
  reviewDate?: string,
  partNoDetails?: Pick<PartNo, "partNo" | "displayPartNo">[],
): Promise<{ folderName: string; fileName: string; contentType: string; fileContent: string }[]> => {

  const googleAPI = new GoogleAPI();

  // Google API 初期化・認証
  if (!await googleAPI.initGapi()) {
    throw new Error("Failed to initialize Google API.");
  }
  if (!await googleAPI.signIn()) {
    throw new Error("Failed to authenticate Google account.");
  }

  const snapshotFiles: { folderName: string; fileName: string; contentType: string; fileContent: string }[] = [];

  // 通貨マスタ取得（通貨ID→通貨名変換用）
  const currencyMap = await fetchCurrencyData(webApiRequest, projectId);

  // 年月リスト生成（プロジェクト期間ベース。BomInfoListの出力と完全一致させるため）
  const yearMonths = generateYearMonthList(startDate, endDate);
  // プロジェクト期間が空/不正で年月リストが作れない場合は、スナップショット内容が欠落するため明示的に中断
  if (yearMonths.length === 0) {
    throw new Error(`Failed to create BOM snapshot: project period (production start date / EOL date) is not set or invalid. (startDate="${startDate}", endDate="${endDate}")`);
  }

  // ファイル名に付与する年月日（yyyyMMdd）
  const now = new Date();
  const dateSuffix = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

  // 振り返りモード時は review_{reviewDate}/BomSnapshot 配下に保存
  const bomFolderName = reviewDate
    ? `${projectId}/review_${reviewDate}/BomSnapshot`
    : `${projectId}/BomSnapshot`;

  // 案B: フォルダパスはpartNo全件で同一のため、ループ外で1回だけ解決する
  const spreadSheetPath = `${SPREAD_SHEET_PATH}/${encodeURIComponent(projectId)}`;
  const parentId = await googleAPI.createFolderPath(spreadSheetPath, null);
  if (!parentId) {
    throw new Error("Failed to create BOM snapshot: could not create folder in My Drive.");
  }

  // 案Z: partNo毎の処理を並列化（独立処理のためPromise.allで一括実行）
  // 案C: 案A(export後即削除)により前回残骸は通常ない。既存SS検索(getFileOrFolderId)は削除済み。
  //      異常終了で残った場合は同名SSが複数作成されるが、機能上は問題なし（fileIdで識別されるため）
  const processPartNo = async (partNo: string): Promise<{ folderName: string; fileName: string; contentType: string; fileContent: string }> => {
    // 画面表示用PartNo（No.435対応。displayPartNo未登録ならpartNoにフォールバック）
    const displayPartNo = resolveDisplayPartNoByKey(partNo, partNoDetails);
    // DBからBOMデータを取得してスナップショット用SSを作成
    const bomData = await fetchBomDataFromDb(webApiRequest, projectId, partNo, reviewDate);
    if (bomData.length === 0) {
      throw new Error(`BOM data not found: ${partNo}`);
    }
    const spreadsheetId = await createSnapshotSpreadsheet(googleAPI, projectId, displayPartNo, bomData, currencyMap, yearMonths, parentId);
    console.log(`Snapshot spreadsheet created for partNo: ${partNo}, id: ${spreadsheetId}`);

    // Excelとしてエクスポート
    const base64 = await exportSpreadsheetAsExcelBase64(spreadsheetId);
    const fileName = `${sanitizeFileName(displayPartNo)}_${dateSuffix}.xlsx`;

    // 案A: Excelエクスポート完了後、マイドライブのSSを削除（ストレージゴミ蓄積対策）
    try {
      await googleAPI.deleteFile(spreadsheetId);
      console.log(`Deleted snapshot spreadsheet after export: ${spreadsheetId}`);
    } catch (e) {
      // 削除失敗してもExcelエクスポートは完了しているため、警告ログのみで続行
      console.warn(`Failed to delete snapshot spreadsheet: ${spreadsheetId}`, e);
    }

    return {
      folderName: bomFolderName,
      fileName,
      contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      fileContent: base64,
    };
  };

  // 案Z: partNo全件を並列実行。1件でも失敗すればPromise.allが即reject→上位でエラーハンドリング
  const results = await Promise.all(partNoList.map(processPartNo));
  snapshotFiles.push(...results);

  return snapshotFiles;
};
